'use client';

import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  FC,
  PropsWithChildren,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { useCookies } from 'react-cookie';

import {
  Gender,
  Style,
  Effect,
  StyleCategory,
  Avatar,
  PhotoSession,
  VideoLook,
  VideoStyle,
  CombiVideo,
  TrendingItem,
  VideoVeo3,
  StoryLine,
  GenerationSource,
} from '@/.gql/graphql';
import {
  GET_STYLE_CATEGORIES,
  SET_ACTIVE_AVATAR,
  GET_STORY_LINES,
} from '@/.graphql';
import { useVisitOffset } from '@/src/hooks/useVisitOffset';
import { useProfileContext } from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';
import { rotateArray } from '@/src/utils/rotateArray';
import { serializeAiVideos } from '@/src/utils/serializeAiVideos';

import { createCustomContext } from './context';

export type TPortalSeeAllMode =
  | 'styles'
  | 'videoStyleCategories'
  | 'effectsCategories'
  | 'photoSessions'
  | 'videoLooks'
  | 'videoStyles'
  | 'combiVideos'
  | 'bestItems'
  | 'effects'
  | 'limited'
  | 'stickers'
  | 'aiVideos'
  | 'videoVeo3'
  | 'storyLines';

export type TPrevPortalMode =
  | 'main'
  | 'seeAll'
  | 'trending'
  | 'menu'
  | 'allTools'
  | 'storyline';

export type TAiVideo = {
  name: Record<string, string>;
  id: string;
  __typename: 'VideoLook' | 'VideoStyle';
  limitedUntil?: Date;
  isBest: boolean;
  isLimited?: boolean;
  showResolutions: boolean;
  videoStyleId?: string;
  videoLookId?: string;
  gendersShown: Array<Gender>;
  imageMan?: string | null;
  imageWoman?: string | null;
  isNew: boolean;
  isPopular: boolean;
  videoMan?: string | null;
  videoWoman?: string | null;
  styleCategoryId: string | null;
};

export type AiVideosStyleCategory = Omit<
  StyleCategory,
  'videoLooks' | 'videoStyles'
> & {
  aiVideos: TAiVideo[];
};

export type TActualItem =
  | PhotoSession
  | VideoLook
  | Style
  | VideoStyle
  | CombiVideo
  | Effect
  | TAiVideo
  | VideoVeo3
  | StoryLine;

interface IProps {
  styleCategories: StyleCategory[];
  videoStyleCategories: AiVideosStyleCategory[];
  effectsCategories: StyleCategory[];
  activeCategory: StyleCategory | AiVideosStyleCategory;
  portalSeeAllMode: TPortalSeeAllMode;
  activeStyle: TActualItem;
  isPhotosessionPhoto: boolean;
  isAvatarExist: boolean;
  isVideoExist: boolean;
  changeActiveCategory: (
    categoryId: string,
    mode: 'style' | 'video' | 'effects',
  ) => void;
  changeActiveStyle: (styleId: string, mode: TPortalSeeAllMode) => void;
  videoLooks: VideoLook[];
  videoStyles: VideoStyle[];
  videoVeo3: VideoVeo3[];
  combiVideos: CombiVideo[];
  aiVideos: TAiVideo[];
  photoSessions: PhotoSession[];
  bestItems: TActualItem[];
  limited: TActualItem[];
  effects: Effect[];
  stickers: Effect[];
  trendingItems: TrendingItem[];
  storyLines: StoryLine[];
  genderMode: Gender;
  setGenderMode(value: Gender): void;
  setPortalSeeAllMode(value: TPortalSeeAllMode): void;
  activeAspectRatio: string;
  setActiveAspectRatio(value: string): void;
  activeAvatar: string;
  setActiveAvatar(value: string): void;
  profileAvatars: Array<Avatar>;
  filteredAvatars: Array<Avatar>;
  activeExample: string;
  setActiveExample: Dispatch<SetStateAction<string>>;
  isYourPhoto: boolean;
  setIsYourPhoto: Dispatch<SetStateAction<boolean>>;
  currentPack:
    | Style[]
    | PhotoSession[]
    | VideoLook[]
    | VideoStyle[]
    | CombiVideo[]
    | TActualItem[]
    | TAiVideo[];
  prevPortal: TPrevPortalMode;
  setPrevPortal: Dispatch<SetStateAction<TPrevPortalMode>>;
}

const [Context, useStyleContext] = createCustomContext<IProps>({
  name: 'ImagesContext',
});
export { useStyleContext };

export const StyleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [$setActiveAvatar] = useMutation(SET_ACTIVE_AVATAR);
  const [cookies, setCookie] = useCookies([
    '_genderMode',
    'aspectRatioIndex',
    'aspectRatio',
    'activeAvatar',
  ]);
  const profile = useProfileContext()!;
  const visitOffset = useVisitOffset(11);

  const profileAvatars = useMemo(
    () =>
      (profile?.avatars ?? []).filter(
        (a) => a.gender === Gender.Female || a.gender === Gender.Male,
      ),
    [profile],
  );

  const defaultAvatar = useMemo(
    () =>
      profileAvatars?.some((a) => a.id === cookies?.activeAvatar)
        ? profileAvatars?.find((a) => a.id === cookies?.activeAvatar)
        : profileAvatars && profileAvatars[0],
    [profileAvatars, cookies],
  );

  const [activeAvatar, setActiveAvatar] = useState<string>(
    defaultAvatar?.id ?? '',
  );

  const [isYourPhoto, setIsYourPhoto] = useState<boolean>(false);

  // eslint-disable-next-line prefer-const
  let [genderMode, setGenderMode] = useState<Gender>(
    defaultAvatar?.gender ?? Gender.Female,
  );

  const filteredAvatars = useMemo(() => {
    const avatars = profileAvatars ?? [];

    return avatars.filter((avatar) => avatar.gender === genderMode);
  }, [profileAvatars, genderMode]);

  const isAvatarExist = useMemo(
    () => filteredAvatars?.some((avatar) => avatar.id === activeAvatar),
    [activeAvatar, filteredAvatars],
  );

  const [portalSeeAllMode, setPortalSeeAllMode] =
    useState<TPortalSeeAllMode>('styles');

  const [prevPortal, setPrevPortal] = useState<TPrevPortalMode>('main');

  const { data: queryData } = useSuspenseQuery(GET_STYLE_CATEGORIES);
  const queryResult = queryData || {};

  const { data: storyLinesData } = useSuspenseQuery(GET_STORY_LINES);
  const storyLinesResult = storyLinesData || {};

  const filterByGender = useCallback(
    <T extends { gendersShown: Gender[] } & (TActualItem | TrendingItem)>(
      items: T[],
    ): T[] =>
      items.filter(
        (item) =>
          item.gendersShown.includes(genderMode) &&
          Boolean(getImageByGender(item, genderMode)),
      ),
    [genderMode],
  );

  const filterCategoryByGender = useCallback(
    <T extends { gendersShown: Gender[] } & StyleCategory>(items: T[]): T[] =>
      items.filter((item) => item.gendersShown.includes(genderMode)),
    [genderMode],
  );

  const styleCategories = useMemo(() => {
    const mapped = filterCategoryByGender(queryResult?.getStyleCategories)?.map(
      (category) => ({
        ...category,
        styles: filterByGender(category.styles).filter((s) => !s.isLimited),
      }),
    );
    const filtered = mapped.filter(({ styles }) => styles.length);
    const rotated = rotateArray(filtered, visitOffset);

    return rotated;
  }, [
    filterCategoryByGender,
    queryResult?.getStyleCategories,
    visitOffset,
    filterByGender,
  ]);

  const effectsCategories = useMemo(() => {
    const mapped = filterCategoryByGender(queryResult?.getStyleCategories)?.map(
      (category) => ({
        ...category,
        effects: filterByGender(category.effects).filter((s) => !s.isLimited),
      }),
    );
    const filtered = mapped.filter(({ effects }) => effects.length);
    const rotated = rotateArray(filtered, visitOffset);

    return rotated;
  }, [
    filterCategoryByGender,
    queryResult?.getStyleCategories,
    visitOffset,
    filterByGender,
  ]);

  const videoStyleCategories = useMemo(() => {
    const categories =
      filterCategoryByGender(queryResult?.getStyleCategories)?.map(
        ({ videoStyles, videoLooks, ...rest }) => {
          const styles = filterByGender(videoStyles).filter(
            ({ isLimited }) => !isLimited,
          );
          const looks = filterByGender(videoLooks).filter(
            ({ isLimited }) => !isLimited,
          );

          return {
            ...rest,
            aiVideos: rotateArray(
              serializeAiVideos(styles, looks),
              visitOffset,
            ),
          };
        },
      ) || [];
    const filtered = categories.filter(({ aiVideos }) => aiVideos.length);
    return filtered;
  }, [
    filterCategoryByGender,
    queryResult?.getStyleCategories,
    filterByGender,
    visitOffset,
  ]);

  const styleCategoriesLimited = useMemo(() => {
    const mapped = filterCategoryByGender(queryResult?.getStyleCategories)?.map(
      (category) => ({
        ...category,
        styles: filterByGender(category.styles).filter((s) => s.isLimited),
      }),
    );
    const filtered = mapped.filter(({ styles }) => styles.length);

    return filtered;
  }, [filterByGender, filterCategoryByGender, queryResult?.getStyleCategories]);

  const photoSessions = useMemo(
    () =>
      rotateArray(
        filterByGender(queryResult?.getPhotoSessions) || [],
        visitOffset,
      ),
    [queryResult?.getPhotoSessions, filterByGender, visitOffset],
  );

  const videoLooks = useMemo(
    () =>
      filterByGender(queryResult?.getVideoLooks).filter(
        ({ styleCategoryId }) => !styleCategoryId,
      ) || [],
    [filterByGender, queryResult?.getVideoLooks],
  );

  const videoStyles = useMemo(
    () =>
      filterByGender(queryResult?.getVideoStyles)?.filter(
        ({ styleCategoryId }) => !styleCategoryId,
      ) || [],
    [filterByGender, queryResult?.getVideoStyles],
  );

  const combiVideos = useMemo(
    () => filterByGender(queryResult?.getCombiVideos) || [],
    [queryResult?.getCombiVideos, filterByGender],
  );

  const videoVeo3 = useMemo(
    () => filterByGender(queryResult?.getVideoVeo3) || [],
    [queryResult?.getVideoVeo3, filterByGender],
  );

  const aiVideos = useMemo<TAiVideo[]>(
    () => rotateArray(serializeAiVideos(videoStyles, videoLooks), visitOffset),
    [videoStyles, videoLooks, visitOffset],
  );

  const storyLines = useMemo(
    () => filterByGender(storyLinesResult?.getStoryLines) || [],
    [storyLinesResult?.getStoryLines, filterByGender],
  );

  const trendingItems = useMemo(
    () =>
      rotateArray(
        filterByGender(queryResult?.getTrending).sort(
          (a, b) => a.position - b.position,
        ) || [],
        visitOffset,
      ),
    [filterByGender, queryResult?.getTrending, visitOffset],
  );

  const effects = useMemo(
    () =>
      rotateArray(
        filterByGender(queryResult?.getEffects).filter(
          ({ isStickerPack, styleCategoryId }) =>
            !isStickerPack && !styleCategoryId,
        ) || [],
        visitOffset,
      ),
    [queryResult?.getEffects, filterByGender, visitOffset],
  );

  const stickers = useMemo(() => {
    const allStickers =
      filterByGender(queryResult?.getEffects).filter(
        ({ isStickerPack, styleCategoryId }) =>
          isStickerPack && !styleCategoryId,
      ) || [];
    // Move sticker with id "aljdsklasjdSDa" to the front if it exists
    const idx = allStickers.findIndex(
      (sticker) => sticker.id === 'aljdsklasjdSDa',
    );
    if (idx > -1) {
      const [specialSticker] = allStickers.splice(idx, 1);
      allStickers.unshift(specialSticker);
    }
    return rotateArray(allStickers, visitOffset);
  }, [queryResult?.getEffects, filterByGender, visitOffset]);

  const bestItems = useMemo(() => {
    const allItems = [
      ...photoSessions,
      ...aiVideos,
      ...combiVideos,
      ...effects,
      ...stickers,
      ...videoVeo3,
      ...storyLines,
      ...styleCategories.flatMap((category) => category.styles),
      ...videoStyleCategories.flatMap((category) => category.aiVideos),
      ...effectsCategories.flatMap((category) => category.effects),
    ];

    const allBestItems = allItems.filter((item) => item.isBest);

    return rotateArray(allBestItems, visitOffset);
  }, [
    photoSessions,
    aiVideos,
    combiVideos,
    effects,
    stickers,
    styleCategories,
    videoStyleCategories,
    effectsCategories,
    visitOffset,
    videoVeo3,
    storyLines,
  ]);

  const limited = useMemo(() => {
    const allItems = [
      ...photoSessions,
      ...aiVideos,
      ...combiVideos,
      ...effects,
      ...videoVeo3,
      ...storyLines,
      ...styleCategoriesLimited.flatMap((category) => category.styles),
    ];

    const now = Date.now();

    const isStillValid = (item: { limitedUntil?: Date }) => {
      if (!item.limitedUntil) {
        return false;
      }
      const date = new Date(item.limitedUntil);
      return date.getTime() > now;
    };

    const allLimitedItems = allItems.filter(
      (item) => item.isLimited && isStillValid(item),
    );

    allLimitedItems.sort((a, b) => {
      const dateA = new Date(a.limitedUntil!).getTime();
      const dateB = new Date(b.limitedUntil!).getTime();
      return dateA - dateB;
    });

    return allLimitedItems;
  }, [
    photoSessions,
    aiVideos,
    videoVeo3,
    combiVideos,
    storyLines,
    effects,
    styleCategoriesLimited,
  ]);

  const [activeCategory, setActiveCategory] = useState<
    StyleCategory | AiVideosStyleCategory
  >(styleCategories[0]);
  const [activeStyle, setActiveStyle] = useState<TActualItem>(
    activeCategory?.styles[0],
  );

  const isPhotosessionPhoto =
    activeStyle?.__typename === 'PhotoSession' &&
    activeStyle?.generationSource === GenerationSource.Photo;

  const [activeExample, setActiveExample] = useState('dd');

  const [activeAspectRatio, setActiveAspectRatio] = useState(
    cookies?.aspectRatio ?? '9:16',
  );

  const currentPack = useMemo(() => {
    switch (portalSeeAllMode) {
      case 'styles':
        return activeCategory?.styles;
      case 'videoStyleCategories':
        return (activeCategory as AiVideosStyleCategory)?.aiVideos;
      case 'effectsCategories':
        return activeCategory?.effects;
      case 'photoSessions':
        return photoSessions;
      case 'videoLooks':
        return videoLooks;
      case 'videoStyles':
        return videoStyles;
      case 'combiVideos':
        return combiVideos;
      case 'bestItems':
        return bestItems;
      case 'effects':
        return effects;
      case 'limited':
        return limited;
      case 'stickers':
        return stickers;
      case 'aiVideos':
        return aiVideos;
      case 'videoVeo3':
        return videoVeo3;
      case 'storyLines':
        return storyLines;
      default:
        return [];
    }
  }, [
    portalSeeAllMode,
    activeCategory,
    photoSessions,
    videoLooks,
    videoStyles,
    combiVideos,
    bestItems,
    effects,
    limited,
    stickers,
    aiVideos,
    videoVeo3,
  ]);

  const changeActiveCategory = (
    categoryId: string,
    mode: 'style' | 'video' | 'effects',
  ) => {
    if (categoryId) {
      let category;
      if (mode === 'style') {
        category = styleCategories.find(
          (category) => category.id === categoryId,
        );
      } else if (mode === 'video') {
        category = videoStyleCategories.find(
          (category) => category.id === categoryId,
        );
      } else {
        category = effectsCategories.find(
          (category) => category.id === categoryId,
        );
      }

      if (!category) {
        return;
      }

      setActiveCategory(category);
    }
  };

  const changeActiveStyle = useCallback(
    (styleId: string, mode: TPortalSeeAllMode) => {
      let currentStyle: TActualItem | undefined;

      if (mode === 'styles') {
        currentStyle = styleCategories
          .flatMap((category) => category.styles)
          .find((style) => style.id === styleId);
      } else if (mode === 'videoStyleCategories') {
        currentStyle = videoStyleCategories
          .flatMap((category) => category.aiVideos)
          .find((style) => style.id === styleId);
      } else if (mode === 'effectsCategories') {
        currentStyle = effectsCategories
          .flatMap((category) => category.effects)
          .find((style) => style.id === styleId);
      } else {
        const stylesMap: Record<
          | 'photoSessions'
          | 'videoLooks'
          | 'videoStyles'
          | 'combiVideos'
          | 'bestItems'
          | 'effects'
          | 'limited'
          | 'stickers'
          | 'aiVideos'
          | 'videoVeo3'
          | 'storyLines',
          TActualItem[]
        > = {
          photoSessions,
          videoLooks,
          videoStyles,
          combiVideos,
          bestItems,
          effects,
          limited,
          stickers,
          aiVideos,
          videoVeo3,
          storyLines,
        };

        currentStyle = stylesMap[mode]?.find((item) => item.id === styleId);
      }

      if (currentStyle) {
        setActiveStyle(currentStyle);
      }
    },
    [
      styleCategories,
      videoStyleCategories,
      effectsCategories,
      photoSessions,
      videoLooks,
      videoStyles,
      combiVideos,
      bestItems,
      effects,
      limited,
      stickers,
      aiVideos,
      videoVeo3,
      storyLines,
    ],
  );

  const isVideoExist = useMemo(
    () =>
      Boolean(videoLooks.length && videoStyles.length && combiVideos.length),
    [videoLooks.length, videoStyles.length, combiVideos.length],
  );

  const onSetGenderMode = (value: Gender) => {
    setCookie('_genderMode', value, { path: '/', maxAge: 15552000 });
    setGenderMode(value);
  };
  const onSetAspectRatio = (value: string) => {
    setCookie('aspectRatio', value, { path: '/', maxAge: 15552000 });
    setActiveAspectRatio(value);
  };
  const onSetActiveAvatar = async (value: string) => {
    setCookie('activeAvatar', value, { path: '/', maxAge: 15552000 });
    setActiveAvatar(value);
    await $setActiveAvatar({
      variables: { avatarId: value },
    }).catch((error) => {
      console.error('Error setting active avatar:', error);
    });
  };

  return (
    <Context
      value={{
        styleCategories,
        videoStyleCategories,
        effectsCategories,
        activeCategory,
        activeStyle,
        photoSessions,
        videoLooks,
        videoStyles,
        combiVideos,
        videoVeo3,
        storyLines,
        trendingItems,
        bestItems,
        changeActiveCategory,
        changeActiveStyle,
        genderMode,
        setGenderMode: onSetGenderMode,
        activeAspectRatio,
        setActiveAspectRatio: onSetAspectRatio,
        activeAvatar,
        isYourPhoto,
        setIsYourPhoto,
        isAvatarExist,
        setActiveAvatar: onSetActiveAvatar,
        profileAvatars,
        filteredAvatars,
        activeExample,
        setActiveExample,
        portalSeeAllMode,
        setPortalSeeAllMode,
        prevPortal,
        setPrevPortal,
        currentPack,
        effects,
        stickers,
        limited,
        isVideoExist,
        aiVideos,
        isPhotosessionPhoto,
      }}
    >
      {children}
    </Context>
  );
};
